import {ReactNode} from 'react';

import {AsyncSearchDropdownDataFetcher} from '../../../../../../libs/shared/ui/src/components/dropdowns/search/asyncSearchDropdown';
import {IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {SearchQueryPart} from '../../../../../../srcCommon/inboxes/search/searchQueryHelpers';

/*
 * Types.
 */

export enum InboxesSearchQueryBuilderInputsEnum {
  ACCOUNT = 'account',
  BODY_CONTAINS = 'body_contains',
  FROM = 'from',
  STATUS = 'status',
  SUBJECT = 'subject',
  TAG = 'tag',
  TO = 'to',
}

export interface InputValue {
  queryPart?: SearchQueryPart;
  modifier?: ModifiersEnum;
  value: string;
}

export interface InboxesSearchQueryBuilderBaseInputConfig {
  id: InboxesSearchQueryBuilderInputsEnum;
  modifier: ModifiersEnum;
  icon: IconName;
  label: string;
  placeholder: string;
}

export interface InboxesSearchQueryBuilderRecipientInputConfig
  extends InboxesSearchQueryBuilderBaseInputConfig {
  namespace?: string;
}

export interface SyncDropdownInputProps {
  icon: IconName;
  inputValue: InputValue;
  label: ReactNode;
  textLabel: string;
}

export interface InboxesSearchQueryBuilderSyncDropdownInputConfig
  extends InboxesSearchQueryBuilderBaseInputConfig {
  title: ReactNode;
  items: ReadonlyArray<SyncDropdownInputProps>;
}

export interface InboxesSearchQueryBuilderAsyncDropdownInputConfig<T>
  extends InboxesSearchQueryBuilderBaseInputConfig {
  title: ReactNode;
  fetcher: AsyncSearchDropdownDataFetcher<T, any>;
  makeRowRenderer: (
    selectedValue: InputValue,
    onSelect: (value: InputValue) => void,
  ) => (item: T, index: number, items: ReadonlyArray<T>) => ReactNode;
}

export type InboxesSearchQueryBuilderInputConfig<T = any> =
  | InboxesSearchQueryBuilderBaseInputConfig
  | InboxesSearchQueryBuilderRecipientInputConfig
  | InboxesSearchQueryBuilderSyncDropdownInputConfig
  | InboxesSearchQueryBuilderAsyncDropdownInputConfig<T>;

export interface InboxesSearchQueryBuilderInputValue {
  inputValue: InputValue;
  onChange: (value: InputValue) => void;
  onSearch?: () => void;
}

/*
 * Type guards.
 */

export function isRecipientInputConfig(
  inputConfig: InboxesSearchQueryBuilderInputConfig,
): inputConfig is InboxesSearchQueryBuilderRecipientInputConfig {
  return 'namespace' in inputConfig;
}

export function isSyncDropdownInputConfig(
  inputConfig: InboxesSearchQueryBuilderInputConfig,
): inputConfig is InboxesSearchQueryBuilderSyncDropdownInputConfig {
  return 'title' in inputConfig && 'items' in inputConfig;
}

export function isAsyncDropdownInputConfig<T>(
  inputConfig: InboxesSearchQueryBuilderInputConfig,
): inputConfig is InboxesSearchQueryBuilderAsyncDropdownInputConfig<T> {
  return 'title' in inputConfig && 'fetcher' in inputConfig && 'makeRowRenderer' in inputConfig;
}
